"use client";

import { useEffect } from "react";

declare global {
  interface Window {
    _fbq?: unknown;
    __vetinaAnalyticsBooted?: boolean;
    __vetinaMetaPixelBooted?: boolean;
  }
}

const GA_MEASUREMENT_ID = "G-K08PTL06HB";
const META_PIXEL_ID = "1596294028827921";
const COOKIE_CONSENT_KEY = "vetina-cookie-consent";
const CONSENT_EVENT = "vetina:consent-changed";

type Consent = "all" | "necessary" | null;

function readConsent(): Consent {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { consent?: Consent };
    return parsed.consent ?? null;
  } catch {
    return null;
  }
}

const PHONE_REGEX = /\b(?:\+?\d{1,3}[\s-]?)?(?:\(?\d{2,4}\)?[\s-]?)?\d{3}[\s-]?\d{3}[\s-]?\d{3,4}\b/g;
const EMAIL_REGEX = /[\w.+-]+@[\w-]+\.[\w.-]+/g;

function scrubPII(value: unknown): unknown {
  if (typeof value === "string") {
    return value.replace(PHONE_REGEX, "[REDACTED_PHONE]").replace(EMAIL_REGEX, "[REDACTED_EMAIL]");
  }
  if (Array.isArray(value)) return value.map(scrubPII);
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (/phone|email|tel|mail/i.test(k) && typeof v === "string") {
        out[k] = "[REDACTED]";
      } else {
        out[k] = scrubPII(v);
      }
    }
    return out;
  }
  return value;
}

let posthogInitPromise: Promise<void> | null = null;

function initPostHog(consent: Consent) {
  if (posthogInitPromise) return posthogInitPromise;
  posthogInitPromise = (async () => {
    const posthogModule = await import("posthog-js");
    const posthog = posthogModule.default;
    const token = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN;
    if (!token) return;
    posthog.init(token, {
      api_host: "/ingest",
      ui_host: "https://eu.posthog.com",
      defaults: "2026-01-30",
      opt_out_capturing_by_default: consent !== "all",
      persistence: "localStorage+cookie",
      debug: process.env.NODE_ENV === "development",
      disable_surveys: true,
      capture_dead_clicks: false,
      capture_performance: { web_vitals: false, network_timing: false },
      session_recording: {
        maskAllInputs: false,
        maskInputOptions: { password: true, email: true, tel: true },
        maskTextSelector: "[data-ph-mask]",
      },
      before_send: (event) => {
        if (!event) return event;
        if (event.properties) {
          event.properties = scrubPII(event.properties) as typeof event.properties;
        }
        return event;
      },
      loaded: (ph) => {
        if (typeof window === "undefined") return;
        ph.register_once({
          initial_landing_page: window.location.pathname,
          initial_landing_url: window.location.href,
          initial_referrer_host: document.referrer ? new URL(document.referrer).hostname : "$direct",
        });
      },
    });
    if (consent === "all") {
      posthog.opt_in_capturing();
    }
  })();
  return posthogInitPromise;
}

let gaInjected = false;
function injectGA(consent: Consent) {
  if (gaInjected) return;
  gaInjected = true;
  const w = window;
  w.dataLayer = w.dataLayer || [];
  w.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer!.push(arguments as unknown as Record<string, unknown>);
  };
  w.gtag("consent", "default", {
    analytics_storage: consent === "all" ? "granted" : "denied",
    ad_storage: consent === "all" ? "granted" : "denied",
  });
  w.gtag("js", new Date());
  w.gtag("config", GA_MEASUREMENT_ID);
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(s);
}

function injectMetaPixel() {
  if (typeof window === "undefined") return;
  if (window.__vetinaMetaPixelBooted) return;
  window.__vetinaMetaPixelBooted = true;
  const w = window as unknown as Record<string, unknown>;
  if (w.fbq) {
    (window.fbq as (...args: unknown[]) => void)("init", META_PIXEL_ID);
    (window.fbq as (...args: unknown[]) => void)("track", "PageView");
    return;
  }
  const stub: Record<string, unknown> = function fbqStub(...args: unknown[]) {
    const queue = (stub.queue as unknown[]) || [];
    const callMethod = stub.callMethod as ((...a: unknown[]) => void) | undefined;
    if (callMethod) callMethod.apply(stub, args);
    else queue.push(args);
  } as unknown as Record<string, unknown>;
  stub.push = stub;
  stub.loaded = true;
  stub.version = "2.0";
  stub.queue = [];
  w.fbq = stub;
  if (!w._fbq) w._fbq = stub;
  const t = document.createElement("script");
  t.async = true;
  t.src = "https://connect.facebook.net/en_US/fbevents.js";
  const s = document.getElementsByTagName("script")[0];
  s.parentNode!.insertBefore(t, s);
  (window.fbq as (...args: unknown[]) => void)("init", META_PIXEL_ID);
  (window.fbq as (...args: unknown[]) => void)("track", "PageView");
}

function bootAnalytics() {
  if (typeof window === "undefined") return;
  if (window.__vetinaAnalyticsBooted) return;
  window.__vetinaAnalyticsBooted = true;

  const consent = readConsent();
  initPostHog(consent);
  injectGA(consent);
  if (consent === "all") {
    injectMetaPixel();
  }
}

async function onConsentChange() {
  const consent = readConsent();
  if (!window.__vetinaAnalyticsBooted) {
    bootAnalytics();
    return;
  }
  if (consent === "all") {
    injectMetaPixel();
    try {
      const posthog = (await import("posthog-js")).default;
      posthog.opt_in_capturing();
    } catch {}
    window.gtag?.("consent", "update", {
      analytics_storage: "granted",
      ad_storage: "granted",
    });
  } else {
    window.gtag?.("consent", "update", {
      analytics_storage: "denied",
      ad_storage: "denied",
    });
    try {
      const posthog = (await import("posthog-js")).default;
      posthog.opt_out_capturing();
    } catch {}
  }
}

export function DeferredAnalytics() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.__vetinaAnalyticsBooted) return;

    let fired = false;
    const fire = () => {
      if (fired) return;
      fired = true;
      cleanup();
      bootAnalytics();
    };

    const interactionEvents = ["scroll", "keydown", "mousemove", "touchstart", "pointerdown"] as const;
    const cleanup = () => {
      interactionEvents.forEach((ev) => window.removeEventListener(ev, fire));
      if (timeoutId) window.clearTimeout(timeoutId);
      if (idleId && "cancelIdleCallback" in window) {
        (window as unknown as { cancelIdleCallback: (id: number) => void }).cancelIdleCallback(idleId);
      }
    };

    interactionEvents.forEach((ev) => window.addEventListener(ev, fire, { passive: true, once: true }));

    let idleId: number | undefined;
    const ric = (window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    }).requestIdleCallback;
    if (typeof ric === "function") {
      idleId = ric(fire, { timeout: 4000 });
    }
    const timeoutId = window.setTimeout(fire, 4500);

    window.addEventListener(CONSENT_EVENT, onConsentChange);

    return () => {
      cleanup();
      window.removeEventListener(CONSENT_EVENT, onConsentChange);
    };
  }, []);

  return null;
}

export const CONSENT_CHANGED_EVENT = CONSENT_EVENT;
